// @ts-check
/* eslint-env browser */

import * as turf from '@turf/turf'
import { reactiveStore } from '../reactive-store.js'
import { matchBySSD } from './geotiff-overlay.js'

/**
 * Mapping of Geoman event names to transform types
 * @type {Record<string, string>}
 */
const EVENT_TO_TRANSFORM_TYPE = {
  'gm:dragend': 'drag',
  'gm:rotateend': 'rotate',
  'gm:scaleend': 'scale',
  'gm:editend': 'change'
}

/**
 * State tracking for accumulated rotation during active rotation operations
 * (Note: Module-level singleton shared across all BaseMap instances (assumes single instance per page)
 * @type {{ overlayId: string, totalAngle: number, lastBearing: number, centroid: [number,number], containedIds: Set<string> } | null}
 */
let rotationState = null

/**
 * Returns whether grouped map transforms are enabled for an overlay.
 * @param {string} overlayId
 * @returns {boolean}
 */
function shouldMoveWithOverlay (overlayId) {
  const overlayEntry = reactiveStore.overlayMaps.get().find(e => e.storagePath === overlayId)
  return overlayEntry?.moveWith !== false
}

/**
 * Normalises an angle difference to the range [-180, 180].
 * @param {number} angle - Angle in degrees
 * @returns {number} Normalised angle
 */
function normaliseAngle (angle) {
  while (angle > 180) angle -= 360
  while (angle < -180) angle += 360
  return angle
}

/**
 * Extracts the 4 corners (without the closing duplicate) from an overlay polygon feature.
 * @param {any} overlayFeature
 * @returns {[number,number][] | null}
 */
function getCorners (overlayFeature) {
  const ring = overlayFeature?.geometry?.coordinates?.[0]
  if (!Array.isArray(ring) || ring.length < 4) return null
  const pts = ring.length >= 5 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
    ? ring.slice(0, -1)
    : ring
  return pts.slice(0, 4).map(p => /** @type {[number,number]} */ ([p[0], p[1]]))
}

/**
 * Returns true if a non-overlay feature is fully contained within the overlay polygon.
 * @param {any} feature
 * @param {any} overlayFeature
 * @returns {boolean}
 */
function isContained (feature, overlayFeature) {
  try {
    const geomType = feature?.geometry?.type
    if (!geomType) return false
    if (geomType === 'Point') {
      return turf.booleanPointInPolygon(feature.geometry.coordinates, overlayFeature)
    }
    // turf.booleanWithin doesn't support MultiPolygon so check each polygon separately
    if (geomType === 'MultiPolygon') {
      const polygons = feature.geometry.coordinates
      return polygons.every((polygonCoords) =>
        turf.booleanWithin(turf.polygon(polygonCoords), overlayFeature)
      )
    }
    return turf.booleanWithin(feature, overlayFeature)
  } catch {
    return false
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Bilinear quad interpolation for change mode
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Computes the (u, v) parameters of a point within a quad defined by 4 corners
 * using inverse bilinear interpolation.
 *
 * Corner layout: C0 (u=0,v=0), C1 (u=1,v=0), C2 (u=1,v=1), C3 (u=0,v=1).
 *
 * @param {[number,number]} pt
 * @param {[number,number][]} corners - 4 quad corners [C0, C1, C2, C3]
 * @returns {[number,number] | null} (u, v) parameters, or null if degenerate
 */
export function inverseBilinear (pt, corners) {
  const [x0, y0] = corners[0]
  const [x1, y1] = corners[1]
  const [x2, y2] = corners[2]
  const [x3, y3] = corners[3]

  const a = x1 - x0; const b = x3 - x0; const c = x2 - x1 - x3 + x0
  const d = y1 - y0; const e = y3 - y0; const f = y2 - y1 - y3 + y0
  const px = pt[0] - x0
  const py = pt[1] - y0

  const A = e * c - f * b
  const B = e * a + f * px - py * c - d * b
  const C = d * px - py * a

  let v
  if (Math.abs(A) < 1e-12) {
    if (Math.abs(B) < 1e-12) return null
    v = -C / B
  } else {
    const disc = B * B - 4 * A * C
    if (disc < 0) return null
    const sqrtDisc = Math.sqrt(disc)
    const v1 = (-B + sqrtDisc) / (2 * A)
    const v2 = (-B - sqrtDisc) / (2 * A)
    const in01 = (x) => x >= -0.01 && x <= 1.01
    v = in01(v1) ? v1 : v2
  }

  const denomX = a + c * v
  const denomY = d + f * v
  const u = Math.abs(denomX) > Math.abs(denomY)
    ? (px - b * v) / denomX
    : (py - e * v) / denomY

  return /** @type {[number,number]} */ ([u, v])
}

/**
 * Maps (u, v) parameters to a point within a quad using forward bilinear interpolation.
 *
 * @param {[number,number]} uv
 * @param {[number,number][]} corners - 4 quad corners [C0, C1, C2, C3]
 * @returns {[number,number]}
 */
export function forwardBilinear (uv, corners) {
  const [u, v] = uv
  return [
    (1 - u) * (1 - v) * corners[0][0] + u * (1 - v) * corners[1][0] + u * v * corners[2][0] + (1 - u) * v * corners[3][0],
    (1 - u) * (1 - v) * corners[0][1] + u * (1 - v) * corners[1][1] + u * v * corners[2][1] + (1 - u) * v * corners[3][1]
  ]
}

/**
 * Computes barycentric coordinates of a point within a triangle.
 * Returns null if the point is outside the triangle or the triangle is degenerate.
 *
 * @param {[number,number]} pt - Point to map
 * @param {[number,number]} A - Triangle vertex A
 * @param {[number,number]} B - Triangle vertex B
 * @param {[number,number]} C - Triangle vertex C
 * @returns {{w0: number, w1: number, w2: number} | null} Barycentric weights or null
 */
function pointToBarycentricCoords (pt, A, B, C) {
  const v0 = [B[0] - A[0], B[1] - A[1]]
  const v1 = [C[0] - A[0], C[1] - A[1]]
  const v2 = [pt[0] - A[0], pt[1] - A[1]]

  const denom = v0[0] * v1[1] - v1[0] * v0[1]
  if (Math.abs(denom) < 1e-12) return null

  const w1 = (v2[0] * v1[1] - v1[0] * v2[1]) / denom
  const w2 = (v0[0] * v2[1] - v2[0] * v0[1]) / denom
  const w0 = 1 - w1 - w2

  // Check if inside triangle (with small tolerance)
  if (w0 < -1e-9 || w1 < -1e-9 || w2 < -1e-9) return null

  return { w0, w1, w2 }
}

/**
 * Maps a point from source quad to destination quad using the same 2-triangle
 * piecewise affine approach that MapLibre uses for image sources.
 *
 * Uses triangle split: (C0,C1,C3) and (C1,C3,C2)
 * This matches MapLibre's rasterBounds triangulation where vertices are ordered:
 * V0=C0 (top-left), V1=C1 (top-right), V2=C3 (bottom-left), V3=C2 (bottom-right)
 * Creating triangles: (V0,V1,V2)=(C0,C1,C3) and (V1,V2,V3)=(C1,C3,C2)
 *
 * Corner layout: C0 (u=0,v=0), C1 (u=1,v=0), C2 (u=1,v=1), C3 (u=0,v=1)
 *
 * @param {[number,number]} pt - Point in source quad
 * @param {[number,number][]} srcCorners - 4 source quad corners [C0, C1, C2, C3]
 * @param {[number,number][]} dstCorners - 4 destination quad corners [C0, C1, C2, C3]
 * @returns {[number,number]} Mapped point in destination quad
 */
export function mapPointViaTriangleSplit (pt, srcCorners, dstCorners) {
  const [C0, C1, C2, C3] = srcCorners
  const [D0, D1, D2, D3] = dstCorners

  // Try triangle 0: (C0, C1, C3) - top-left triangle
  const bary0 = pointToBarycentricCoords(pt, C0, C1, C3)
  if (bary0) {
    const { w0, w1, w2 } = bary0
    // Map to destination using triangle 0: (D0, D1, D3)
    return [
      w0 * D0[0] + w1 * D1[0] + w2 * D3[0],
      w0 * D0[1] + w1 * D1[1] + w2 * D3[1]
    ]
  }

  // Try triangle 1: (C1, C3, C2) - bottom-right triangle
  const bary1 = pointToBarycentricCoords(pt, C1, C3, C2)
  if (bary1) {
    const { w0, w1, w2 } = bary1
    // Map to destination using triangle 1: (D1, D3, D2)
    return [
      w0 * D1[0] + w1 * D3[0] + w2 * D2[0],
      w0 * D1[1] + w1 * D3[1] + w2 * D2[1]
    ]
  }

  // Fallback: point is outside both triangles (shouldn't happen for contained features)
  // Use bilinear as fallback
  const uv = inverseBilinear(pt, srcCorners)
  if (!uv) return pt
  return forwardBilinear(uv, dstCorners)
}

// ──────────────────────────────────────────────────────────────────────────────
// Transform application
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Recursively applies a coordinate mapping function to a GeoJSON coordinate
 * structure (handles nested arrays for Polygon, MultiPolygon, etc.).
 * @param {any} coords
 * @param {(pt: [number,number]) => [number,number]} fn
 * @returns {any}
 */
function mapCoords (coords, fn) {
  if (!Array.isArray(coords)) return coords
  if (typeof coords[0] === 'number') return fn(/** @type {[number,number]} */ (coords))
  return coords.map(c => mapCoords(c, fn))
}

/**
 * Applies a grouped transform to all features contained within the selected
 * overlay, based on how the overlay polygon changed.
 *
 * Reads the old overlay state from reactiveStore.features (which hasn't been
 * updated yet at call time) and compares it against the new state in `features`.
 *
 * Mutates the features array in-place and returns it.
 *
 * @param {any[]} features - All features collected from Geoman (new state)
 * @param {string} eventName - The Geoman event name (e.g. 'gm:dragend', 'gm:rotateend', 'gm:editend')
 * @returns {any[]} The same features array (mutated)
 */
export function applyGroupTransform (features, eventName) {
  const transformType = EVENT_TO_TRANSFORM_TYPE[eventName] ?? null
  if (!transformType) return features

  // Skip rotation transform if we're using accumulated rotation tracking
  if (transformType === 'rotate' && rotationState) return features

  const selectedId = reactiveStore.selectedFeatureId.get()
  if (!selectedId) return features

  const overlayEntry = reactiveStore.overlayMaps.get().find(e => e.storagePath === selectedId)
  if (overlayEntry && overlayEntry.moveWith === false) return features

  const oldFeatures = reactiveStore.features.get()
  const oldOverlay = oldFeatures.find(f =>
    f?.properties?._isMapOverlay && f?.properties?._id === selectedId
  )
  if (!oldOverlay) return features

  const oldCorners = getCorners(oldOverlay)
  if (!oldCorners || oldCorners.length !== 4) return features

  const newOverlay = features.find(f =>
    f?.properties?._isMapOverlay && f?.properties?._id === selectedId
  )
  if (!newOverlay) return features

  const rawNewCorners = getCorners(newOverlay)
  if (!rawNewCorners || rawNewCorners.length !== 4) return features

  const newCorners = matchBySSD(rawNewCorners, oldCorners)

  const containedIds = new Set()
  for (const f of oldFeatures) {
    if (f?.properties?._isMapOverlay) continue
    if (!f?.geometry || !f?.properties?._id) continue
    if (isContained(f, oldOverlay)) {
      containedIds.add(f.properties._id)
    }
  }
  if (containedIds.size === 0) return features

  const oldCentroid = /** @type {[number,number]} */ (turf.centroid(oldOverlay).geometry.coordinates)

  if (transformType === 'drag') {
    const newCentroid = turf.centroid(newOverlay).geometry.coordinates
    const deltaLng = newCentroid[0] - oldCentroid[0]
    const deltaLat = newCentroid[1] - oldCentroid[1]
    if (Math.abs(deltaLng) < 1e-12 && Math.abs(deltaLat) < 1e-12) return features

    for (const f of features) {
      if (!containedIds.has(f?.properties?._id)) continue
      f.geometry.coordinates = mapCoords(f.geometry.coordinates, ([lng, lat]) => [lng + deltaLng, lat + deltaLat])
    }
  } else if (transformType === 'rotate') {
    const newCentroid = turf.centroid(newOverlay).geometry.coordinates
    const oldBearing = turf.bearing(turf.point(oldCentroid), turf.point(oldCorners[0]))
    const newBearing = turf.bearing(turf.point(newCentroid), turf.point(newCorners[0]))
    const angleDeg = newBearing - oldBearing
    if (Math.abs(angleDeg) < 1e-8) return features

    for (const f of features) {
      if (!containedIds.has(f?.properties?._id)) continue
      const rotated = turf.transformRotate(f, angleDeg, { pivot: oldCentroid })
      f.geometry = rotated.geometry
    }
  } else if (transformType === 'scale') {
    const newCentroid = turf.centroid(newOverlay).geometry.coordinates
    // Calculate scale factor by comparing distances from centroid to first corner
    const oldDist = turf.distance(turf.point(oldCentroid), turf.point(oldCorners[0]))
    const newDist = turf.distance(turf.point(newCentroid), turf.point(newCorners[0]))
    const scaleFactor = newDist / oldDist
    if (Math.abs(scaleFactor - 1) < 1e-8) return features

    // `turf.transformScale` anchors around `oldCentroid`, but Geoman's corner-drag actually
    // anchors around the opposite corner, so the overlay centroid drifts. Add the
    // centroid delta to keep contained features aligned with the new overlay.
    const deltaLng = newCentroid[0] - oldCentroid[0]
    const deltaLat = newCentroid[1] - oldCentroid[1]

    // Scale all contained features at once using FeatureCollection
    const containedFeatures = features.filter(f => containedIds.has(f?.properties?._id))
    if (containedFeatures.length > 0) {
      const fc = turf.featureCollection(containedFeatures)
      const scaled = turf.transformScale(fc, scaleFactor, { origin: oldCentroid })

      // Apply centroid shift and update original features
      containedFeatures.forEach((f, i) => {
        f.geometry.coordinates = mapCoords(scaled.features[i].geometry.coordinates, ([lng, lat]) => [lng + deltaLng, lat + deltaLat])
      })
    }
  } else if (transformType === 'change') {
    for (const f of features) {
      if (!containedIds.has(f?.properties?._id)) continue
      f.geometry.coordinates = mapCoords(f.geometry.coordinates, (pt) => {
        return mapPointViaTriangleSplit(pt, oldCorners, newCorners)
      })
    }
  }

  return features
}

// ──────────────────────────────────────────────────────────────────────────────
// Rotation tracking for accumulating angle during gm:rotate events
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Tracks rotation increments during gm:rotate events.
 * Called on each gm:rotate event to accumulate the total rotation angle.
 * @param {string} overlayId - ID of the overlay being rotated
 * @param {any[]} liveFeatures - Current features from Geoman during rotation
 */
export function trackRotation (overlayId, liveFeatures) {
  if (!shouldMoveWithOverlay(overlayId)) {
    rotationState = null
    return
  }

  const overlay = liveFeatures.find(f =>
    f?.properties?._isMapOverlay && f?.properties?._id === overlayId
  )
  if (!overlay) return

  const corners = getCorners(overlay)
  if (!corners || corners.length !== 4) return

  // Initialise rotation state on first rotate event or when rotating a different overlay
  // (Assumes gm:rotateend reliably fires to clear state)
  if (!rotationState || rotationState.overlayId !== overlayId) {
    const oldFeatures = reactiveStore.features.get()
    const oldOverlay = oldFeatures.find(f =>
      f?.properties?._isMapOverlay && f?.properties?._id === overlayId
    )
    if (!oldOverlay) return

    const oldCorners = getCorners(oldOverlay)
    if (!oldCorners || oldCorners.length !== 4) return

    const centroid = /** @type {[number,number]} */ (turf.centroid(oldOverlay).geometry.coordinates)
    const bearing = turf.bearing(turf.point(centroid), turf.point(oldCorners[0]))

    const containedIds = new Set()
    for (const f of oldFeatures) {
      if (f?.properties?._isMapOverlay) continue
      if (!f?.geometry || !f?.properties?._id) continue
      if (isContained(f, oldOverlay)) {
        containedIds.add(f.properties._id)
      }
    }

    rotationState = { overlayId, totalAngle: 0, lastBearing: bearing, centroid, containedIds }
    return
  }

  // Calculate incremental angle and accumulate
  const currentBearing = turf.bearing(turf.point(rotationState.centroid), turf.point(corners[0]))
  const angleDelta = normaliseAngle(currentBearing - rotationState.lastBearing)
  rotationState.totalAngle += angleDelta
  rotationState.lastBearing = currentBearing
}

/**
 * Returns the current rotation state (or null if no rotation is active).
 * Allows other modules to access rotation tracking data.
 * @returns {object | null}
 */
export function getRotationState () {
  return rotationState
}

/**
 * Applies the accumulated rotation and clears state.
 * Called on gm:rotateend to apply the total rotation to contained polygons.
 * @param {any[]} features - All features after rotation
 * @param {string} eventName - The event name
 * @returns {any[]} The mutated features array
 */
export function applyAccumulatedRotation (features, eventName) {
  if (!rotationState || eventName !== 'gm:rotateend') return features

  const { totalAngle, centroid, containedIds } = rotationState

  if (Math.abs(totalAngle) < 1e-8) {
    rotationState = null
    return features
  }

  // Apply the accumulated rotation to contained polygons
  for (const f of features) {
    if (!containedIds.has(f?.properties?._id)) continue
    const rotated = turf.transformRotate(f, totalAngle, { pivot: centroid })
    f.geometry = rotated.geometry
  }

  rotationState = null
  return features
}
